import React from 'react'
import { Modal } from 'uiComponents/popups/modal.new'
import { FormikProvider, useFormik } from 'formik'
import { PricingPlanInstallments } from './installments'
import { PricingPlan } from '../../types'
import { PricingPlanFormValues, pricingPlanFormSchema, pricingPlanToFormValues } from '../../pricingPlanFormSchema'
import { PricingPlanRenewal } from './renewal'
import './configurePricingPlanModal.scss'
import { useTranslation } from 'translations'

interface ConfigurePricingPlanModalProps {
    activePricingPlan: PricingPlan | null
    draftPricingPlan: PricingPlan | null
    onSavePricingPlan: (values: PricingPlanFormValues) => Promise<void>
    onDismiss: () => void
}

export const ConfigurePricingPlanModal = ({
    activePricingPlan,
    draftPricingPlan,
    onSavePricingPlan,
    onDismiss,
}: ConfigurePricingPlanModalProps) => {
    const { t } = useTranslation()
    const [step, setStep] = React.useState(1)

    const activeFormValues = pricingPlanToFormValues(activePricingPlan?.paymentPlans ?? [])
    const editingFormValues = pricingPlanToFormValues(draftPricingPlan?.paymentPlans ?? [])

    const initialValues: PricingPlanFormValues = {
        minimumTerm: editingFormValues?.minimumTerm ?? activeFormValues?.minimumTerm ?? 'P1Y',
        renewAutomatically: editingFormValues?.renewAutomatically ?? activeFormValues?.renewAutomatically ?? true,
        renewIntoMonthly: editingFormValues?.renewIntoMonthly ?? activeFormValues?.renewIntoMonthly ?? false,
        allowYearlyInstallments:
            editingFormValues?.allowYearlyInstallments ?? activeFormValues?.allowYearlyInstallments ?? false,
        allowMonthlyInstallments:
            editingFormValues?.allowMonthlyInstallments ?? activeFormValues?.allowMonthlyInstallments ?? false,
        totalPriceYearly: editingFormValues?.totalPriceYearly ?? null,
        totalPriceMonthly: editingFormValues?.totalPriceMonthly ?? null,
        initialPayment: editingFormValues?.initialPayment ?? null,
        allowPerYearPayment: editingFormValues?.allowPerYearPayment ?? activeFormValues?.allowPerYearPayment ?? false,
        allowUpfrontPayment: editingFormValues?.allowUpfrontPayment ?? activeFormValues?.allowUpfrontPayment ?? false,
        pricePerYear: editingFormValues?.pricePerYear ?? null,
        priceUpfront: editingFormValues?.priceUpfront ?? null,
    }

    const onSubmit = async () => {
        await onSavePricingPlan(formik.values)
    }

    const onNextClick = () => {
        formik.validateForm().then((errors) => {
            if (Object.keys(errors).length > 0) {
                return
            }
            setStep(2)
        })
    }

    const formik = useFormik({
        initialValues,
        onSubmit,
        validationSchema: pricingPlanFormSchema,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
    })

    return (
        <Modal
            title={
                draftPricingPlan
                    ? t('products.pricing.pricingPlans.configureModal.editTitle')
                    : t('products.pricing.pricingPlans.add')
            }
            onDismiss={onDismiss}
        >
            <FormikProvider value={formik}>
                <form name="pricing-plan-form" className="pricing-plan-form">
                    <PricingPlanInstallments
                        formik={formik}
                        visible={step === 1}
                        activePricingPlan={activePricingPlan}
                        onClose={onDismiss}
                        onNextClick={onNextClick}
                    />
                    <PricingPlanRenewal
                        formik={formik}
                        visible={step === 2}
                        onBackClick={() => setStep(1)}
                        onSubmit={onSubmit}
                    />
                </form>
            </FormikProvider>
        </Modal>
    )
}
